/* eslint-disable eqeqeq */
import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import { useSelector, shallowEqual } from "react-redux";
import { Chip, Avatar, makeStyles } from "@material-ui/core";
import { Grid, Table, TableHeaderRow, PagingPanel, TableFilterRow } from "@devexpress/dx-react-grid-bootstrap4";
import {
  SortingState,
  IntegratedSorting,
  PagingState,
  IntegratedPaging,
  FilteringState,
  IntegratedFiltering,
  DataTypeProvider,
} from "@devexpress/dx-react-grid";
import { Card, Button, Modal, OverlayTrigger, Tooltip } from "react-bootstrap";
import moment from "moment-timezone";
import SVG from "react-inlinesvg";
import { useHistory } from "react-router-dom";
import { toAbsoluteUrl } from "../../../../../_metronic/_helpers";
import * as apiUtenti from "../../../../api/users";
import * as apiTarget from "../../../../api/target";
import { icons, roles, roleOptionsForSelect, colors } from "../../../../config/config";
import { EditNewUtente } from "./EditNewUtente";
import Select from "react-select";

// ========== STILI ==========
const useStyles = makeStyles({
  abilitato: {
    width: 25,
    height: 25,
    backgroundColor: "var(--danger)",
  },
  disabilitato: {
    width: 25,
    height: 25,
    border: "1px solid var(--gray)",
    borderRadius: "100%",
    backgroundColor: "var(--white)",
  },
});

// ========== COMPONENTI PERSONALIZZATI ==========

// Modale eliminazione utente
const DeleteModal = React.memo(({ name, show, hideCallback, deleteCallback }) => (
  <Modal show={show} onHide={hideCallback} className="card-custom gutter-b">
    <Modal.Header className="border-0 py-5">
      <Modal.Title>
        <h3 className="font-weight-bolder text-dark">Elimina utente</h3>
      </Modal.Title>
    </Modal.Header>
    <Modal.Body className="p-4">Sei sicuro di voler eliminare definitivamente "{name}"?</Modal.Body>
    <Modal.Footer className="border-0 py-5">
      <Button onClick={deleteCallback} variant="danger" className="float-right">
        Elimina
      </Button>
      <Button onClick={hideCallback} variant="secondary" className="float-right">
        Annulla
      </Button>
    </Modal.Footer>
  </Modal>
));

DeleteModal.propTypes = {
  name: PropTypes.string,
  show: PropTypes.bool.isRequired,
  hideCallback: PropTypes.func.isRequired,
  deleteCallback: PropTypes.func.isRequired,
};

// Tabella principale
const TableComponent = React.memo(({ ...restProps }) => (
  <Table.Table {...restProps} className="table table-hover table-head-custom table-vertical-center overflow-hidden" />
));

// Toolbar con pulsante aggiungi
const TabellaCampioniToolbar = React.memo(({ addCallback, ...props }) => (
  <div {...props}>
    <Button onClick={addCallback} className="float-right">
      Aggiungi dipendente
    </Button>
  </div>
));

// Colonna formattata: Data
const DateFormattedColumn = ({ for: columnNames }) => (
  <DataTypeProvider for={columnNames} formatterComponent={({ value }) => moment(value).format("DD MMMM YYYY")} />
);

DateFormattedColumn.propTypes = {
  for: PropTypes.arrayOf(PropTypes.string).isRequired,
};

const TargetFormatter = React.memo(({ value, targets = [] }) => {
  const targetNames = (value || [])
    .map((id) => targets.find((t) => Number(parseInt(t.id, 10)) === Number(parseInt(id, 10)))?.name)
    .filter(Boolean)
    .join(", ");

  return targetNames || "Nessun target";
});

TargetFormatter.propTypes = {
  value: PropTypes.arrayOf(PropTypes.number),
  targets: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
    })
  ),
};

const TargetFormattedColumn = ({ for: columnNames, targets }) => (
  <DataTypeProvider
    for={columnNames}
    formatterComponent={({ value }) => <TargetFormatter value={value} targets={targets} />}
  />
);

TargetFormattedColumn.propTypes = {
  for: PropTypes.arrayOf(PropTypes.string).isRequired,
  targets: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
    })
  ).isRequired,
};

// Colonna formattata: Ruoli
const AziendaColumn = React.memo(({ value }) => {
  const role = parseInt(value, 10);

  const getChip = (condition, label, colorKey) => {
    if (!condition) return null;
    return (
      <Chip
        style={{ color: colors[colorKey], borderColor: colors[colorKey] }}
        variant="outlined"
        avatar={<Avatar style={{ backgroundColor: colors[colorKey], color: "#fff" }}>{label.charAt(0)}</Avatar>}
        label={label}
      />
    );
  };

  return (
    <div className="pl-3">
      {getChip(role === roles.admin, "Admin", "admin")}
      {getChip(role === roles.gestore, "Gestore", "gestore")}
      {getChip(role === roles.developer, "Developer", "developer")}
      {getChip(role === roles.contabile, "Contabile", "contabile")}
      {getChip(role === roles.grafico, "Grafico", "grafico")}
      {getChip(role === roles.agenteRoypet, "Roypet", "agenteRoypet")}
      {getChip(role === roles.agenteRoydermal, "Roydermal", "agenteRoydermal")}
      {getChip(role === roles.agenteValderma, "Valderma", "agenteValderma")}
      {getChip(role === roles.agenteCompleto, "Completo", "agenteCompleto")}
      {getChip(role === roles.magazziniere, "Magazziniere", "magazziniere")}
      {getChip(role === roles.dipendente, "Dipendente", "dipendente")}
    </div>
  );
});

AziendaColumn.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

const AziendaFormattedColumn = React.memo((props) => (
  <DataTypeProvider formatterComponent={AziendaColumn} {...props} />
));

// Vendita abilitata
const AcquistiColumn = React.memo(({ value }) => {
  const classes = useStyles();
  const isAbilitato = !!value;

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100%",
      }}
    >
      <Avatar className={isAbilitato ? classes.abilitato : classes.disabilitato}>
        <span className="svg-icon svg-icon-md svg-icon-white">
          <SVG src={toAbsoluteUrl(icons.ok)} />
        </span>
      </Avatar>
    </div>
  );
});

AcquistiColumn.propTypes = {
  value: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]).isRequired,
};

const AcquistiFormattedColumn = React.memo((props) => (
  <DataTypeProvider formatterComponent={AcquistiColumn} {...props} />
));

// Azioni
const ActionsColumn = React.memo(({ editCallback, deleteCallback }) => (
  <DataTypeProvider
    for={["actions"]}
    formatterComponent={({ value }) => (
      <>
        <OverlayTrigger overlay={<Tooltip>Modifica</Tooltip>}>
          <a
            className="btn btn-icon btn-light btn-hover-primary btn-sm mx-3"
            onClick={() => editCallback(value.id)}
            aria-label="Modifica utente"
          >
            <span className="svg-icon svg-icon-md svg-icon-primary">
              <SVG src={toAbsoluteUrl(icons.edit)} />
            </span>
          </a>
        </OverlayTrigger>

        <OverlayTrigger overlay={<Tooltip>Elimina</Tooltip>}>
          <a
            className="btn btn-icon btn-light btn-hover-danger btn-sm"
            onClick={() => deleteCallback(value.id)}
            aria-label="Elimina utente"
          >
            <span className="svg-icon svg-icon-md svg-icon-danger">
              <SVG src={toAbsoluteUrl(icons.delete)} />
            </span>
          </a>
        </OverlayTrigger>
      </>
    )}
  />
));

ActionsColumn.propTypes = {
  editCallback: PropTypes.func.isRequired,
  deleteCallback: PropTypes.func.isRequired,
};

// Filtro ruoli
const RoleFilterCell = React.memo(({ filter, onFilter }) => {
  const handleRoleChange = (event) => {
    const value = event.value;
    onFilter(value ? { value } : null);
  };

  return (
    <th style={{ fontWeight: "normal" }}>

      <Select
        options={roleOptionsForSelect.map(([value, label]) => ({
            value: Number(parseInt(value, 10)),
            label:label,
          })
        )}
        value={filter?.value ?? ""} // Usa le opzioni convertite
        onChange={handleRoleChange}
        placeholder="Seleziona ruolo..."
        styles={{
          control: (baseStyles) => ({
            ...baseStyles,
            minHeight: 30,
            fontSize: "0.85rem",
          }),
          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
        }}
        menuPortalTarget={document.body}
      />


      {/* <select
        name="role"
        className={`form-control form-control-sm`}
        onChange={handleRoleChange}
        value={filter?.value ?? ""}
      >
        {roleOptionsForSelect.map(([value, label]) => (
          <option key={value} value={value}>
            {label}
          </option>
        ))}
      </select> */}


      {/* <select
        className="form-control form-control-sm"
        value={filter?.value ?? ""}
        onChange={handleRoleChange}
        aria-label="Filtro per ruolo"
      >
        
        <option value="">Seleziona un ruolo</option>
        <option value={roles.admin}>Admin</option>
        <option value={roles.agenteRoypet}>Agente Roypet</option>
        <option value={roles.agenteRoydermal}>Agente Roydermal</option>
        <option value={roles.agenteValderma}>Agente Valderma</option>
        <option value={roles.agenteCompleto}>Agente Completo</option>
        <option value="">------</option>
        <option value={roles.magazziniere}>Magazziniere</option>
        <option value={roles.dipendente}>Dipendente</option>
        <option value={roles.grafico}>Grafico</option>
        <option value={roles.contabile}>Contabile</option>
        <option value={roles.developer}>Developer</option>
      </select> */}
    </th>
  );
});

RoleFilterCell.propTypes = {
  filter: PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  onFilter: PropTypes.func.isRequired,
};

// Checkbox per vendita abilitata
const AcquistiFilterCell = React.memo(({ filter, onFilter }) => {
  const classes = useStyles();
  const handleChange = () => {
    const newValue = !filter?.value;
    onFilter(newValue ? { value: 1 } : 0);
  };

  return (
    <th style={{ fontWeight: "normal", padding: "0.5rem" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
        }}
      >
        <button
          type="button"
          role="checkbox"
          aria-checked={!!filter?.value}
          onClick={handleChange}
          aria-label="Filtro per acquisti abilitati"
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
          }}
        >
          <Avatar className={filter?.value === 1 ? classes.abilitato : classes.disabilitato}>
            <span className="svg-icon svg-icon-md svg-icon-white">
              <SVG src={toAbsoluteUrl(icons.ok)} />
            </span>
          </Avatar>
        </button>
      </div>
    </th>
  );
});

AcquistiFilterCell.propTypes = {
  filter: PropTypes.shape({ value: PropTypes.bool }),
  onFilter: PropTypes.func.isRequired,
};

// Filtro target multi-selezione
const TargetFilterCell = React.memo(({ filter, onFilter, targetOptions }) => {
  const handleChange = (selectedOptions) => {
    if (!selectedOptions || selectedOptions.length === 0) {
      onFilter(null);
    } else {
      const selectedIds = selectedOptions.map((option) => option.value);
      onFilter({
        value: selectedIds,
      });
    }
  };

  // Converti il valore del filtro (array di ID) nelle opzioni corrispondenti
  const selectedValues = useMemo(() => {
    if (!filter?.value || !Array.isArray(filter.value)) return [];
    return targetOptions.filter((option) => filter.value.includes(option.value));
  }, [filter?.value, targetOptions]);

  return (
    <th style={{ fontWeight: "normal", padding: "0.5rem" }}>
      <Select
        isMulti
        options={targetOptions}
        value={selectedValues} // Usa le opzioni convertite
        onChange={handleChange}
        placeholder="Seleziona target..."
        aria-label="Filtro per target"
        styles={{
          control: (baseStyles) => ({
            ...baseStyles,
            minHeight: 30,
            fontSize: "0.85rem",
          }),
          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
        }}
        menuPortalTarget={document.body}
      />
    </th>
  );
});

TargetFilterCell.propTypes = {
  filter: PropTypes.shape({
    value: PropTypes.arrayOf(PropTypes.number), // Era sbagliato prima - ora è array di numeri
  }),
  onFilter: PropTypes.func.isRequired,
  targetOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.number.isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
};

// Filtro custom per colonne
const FilterCell = React.memo(({ column, targetOptions, ...restProps }) => {
  if (column.name === "actions") {
    return (
      <th style={{ fontWeight: "normal" }}>
        <input disabled style={{ visibility: "hidden" }} />
      </th>
    );
  }

  if (column.name === "role") {
    return <RoleFilterCell {...restProps} />;
  }

  if (column.name === "acquisti") {
    return <AcquistiFilterCell {...restProps} />;
  }

  if (column.name === "targets") {
    return <TargetFilterCell {...restProps} targetOptions={targetOptions} />;
  }

  return <TableFilterRow.Cell {...restProps} />;
});

FilterCell.propTypes = {
  column: PropTypes.shape({ name: PropTypes.string }).isRequired,
};

// Componente principale
export const Utenti = () => {
  const history = useHistory();
  const user = useSelector((state) => state.auth.user, shallowEqual);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [targets, setTargets] = useState([]);
  const [updateCount, setUpdateCount] = useState(0);
  const [showEditPage, setShowEditPage] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [utenteForEdit, setUtenteForEdit] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sorting, setSorting] = useState([{ columnName: "fullname", direction: "desc" }]);

  const columns = useMemo(
    () => [
      { name: "fullname", title: "Nome" },
      { name: "role", title: "Ruolo" },
      { name: "acquisti", title: "Vendita" },
      { name: "email", title: "Email" },
      { name: "region", title: "Regione" },
      // { name: "address", title: "Indirizzo" },
      // { name: "phone", title: "Telefono" },
      {
        name: "targets",
        title: "Target",
        getCellValue: (row) => row.targets || [], // Garantisce che sia sempre un array
      },
      { name: "actions", title: "Azioni" },
    ],
    []
  );

  const defaultColumnWidths = [
    { columnName: "role", width: "150" },
    { columnName: "acquisti", width: "100" },
    { columnName: "actions", width: "150" },
  ];

  const tableMessages = useMemo(
    () => ({
      noData: "Nessun dipendente disponibile",
    }),
    []
  );

  const filterRowMessages = useMemo(
    () => ({
      filterPlaceholder: "Filtra...",
    }),
    []
  );

  const pagingPanelMessages = useMemo(
    () => ({
      showAll: "Tutte",
      rowsPerPage: "Righe per pagina",
      info: "Da {from} a {to} ({count} dipendenti)",
    }),
    []
  );

  const pageSizes = useMemo(() => [10, 20, 30, 0], []);

  // Carica dati da API
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiUtenti.getAll(user.authToken);
      const completeData = data.map(row => ({
        ...row,
        id: parseInt(row.id), // Converti ID in numero
        actions: { id: parseInt(row.id) },
        targets: (row.targets || []).map(t => typeof t === 'object' ? Number(parseInt(t.id, 10)) : Number(parseInt(t, 10)))
      }));

      setRows(completeData);
    } catch (error) {
      console.error("Errore nel caricamento:", error);
    } finally {
      setLoading(false);
    }
  }, [user.authToken]);

  const customPredicates = useMemo(
    () => ({
      contains: (value, filter, row) => {
        // Filtro normale per le altre colonne
        if (!filter || !filter.value) return true;
        return String(value)
          .toLowerCase()
          .includes(String(filter.value).toLowerCase());
      },
      targetMatch: (value, filter, row) => {
        // Filtro specifico per i target
        if (!filter?.value || !Array.isArray(filter.value) || filter.value.length === 0) {
          return true;
        }

        const rowTargets = Array.isArray(value) ? value : [];
        const filterTargets = filter.value;

        // Verifica se almeno uno dei target del row è presente nel filtro
        return rowTargets.some((targetId) => filterTargets.includes(targetId));
      },
    }),
    []
  );

  // Aggiorna la funzione getComparatorForType:
  const getComparatorForType = useCallback((columnName) => {
    if (columnName === "targets") {
      return "targetIncludes";
    }
    return undefined;
  }, []);

  // Carica lista target
  const fetchTargets = useCallback(async () => {
    try {
      const data = await apiTarget.getAll(user.authToken);
      setTargets(data);
    } catch (error) {
      console.error("Errore nel caricamento dei target:", error);
    }
  }, [user.authToken]);

  useEffect(() => {
    fetchTargets();
  }, [fetchTargets]);

  // Ricarica dati quando serve
  useEffect(() => {
    fetchData();
  }, [fetchData, updateCount]);

  const rowsRef = useRef(rows);
  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  const [lastHash, setLastHash] = useState("");

  // Gestisce URL hash (#/edit/1 ecc.)
  const handleHashChange = useCallback(() => {
    if (loading || rows.length === 0) {
      // Se i dati non sono pronti, aspetta il prossimo ciclo
      return;
    }

    const hash = window.location.hash;
    console.log('Hash attuale:', hash, 'Dati disponibili:', rows.length);

    try {
      const actions = {
        "#/new": () => {
          console.log('Nuovo utente');
          setUtenteForEdit(null);
          setShowEditPage(true);
          setShowDeleteModal(false);
        },
        "#/delete/": () => {
          const id = parseInt(hash.substring(9), 10);
          console.log('Tentativo eliminazione ID:', id);

          // Converti gli ID in numeri per il confronto
          const row = rows.find(r => Number(r.id) === Number(id));
          if (!row) {
            console.warn('Utente non trovato in rows:', rows);
            window.location.hash = "#/";
            return;
          }

          setUtenteForEdit(row);
          setShowDeleteModal(true);
          setShowEditPage(false);
        },
        "#/edit/": () => {
          const id = parseInt(hash.substring(7), 10);
          console.log('Tentativo modifica ID:', id, 'Utenti disponibili:', rows);

          const row = rows.find(r => Number(r.id) === Number(id));
          if (!row) {
            console.warn(`Utente con ID ${id} non trovato tra:`, rows.map(r => r.id));
            window.location.hash = "#/";
            return;
          }

          setUtenteForEdit(row);
          setShowEditPage(true);
          setShowDeleteModal(false);
        },
        default: () => {
          setShowEditPage(false);
          setShowDeleteModal(false);
        }
      };

      const actionKey = Object.keys(actions).find(key => hash.startsWith(key));
      if (actionKey) {
        console.log('Esecuzione azione per:', actionKey);
        actions[actionKey]();
      } else {
        actions.default();
      }
    } catch (error) {
      console.error("Errore nella gestione dell'hash:", error);
      window.location.hash = "#/";
    }
  }, [loading, rows]);


  useEffect(() => {
    // Gestione iniziale al mount
    if (rows.length > 0 && !loading) {
      handleHashChange();
    }

    // Aggiungi listener per cambiamenti successivi
    window.addEventListener('hashchange', handleHashChange);

    // Cleanup
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [handleHashChange, loading, rows]);

  useEffect(() => {
    const handleInitialLoad = () => {
      if (!loading && rows.length > 0) {
        console.log('Dati caricati, elaboro hash iniziale');
        handleHashChange();
      }
    };

    handleInitialLoad();

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [loading, rows, handleHashChange]);


  const triggerUpdate = useCallback(() => setUpdateCount((c) => c + 1), []);

  const navigateTo = useCallback((path) => {
    if (loading) {
      console.warn('Navigazione bloccata: dati in caricamento');
      return;
    }
    window.location.hash = path;
  }, [loading]);

  const goToEdit = useCallback((id) => {
    if (!id || isNaN(id)) {
      console.error('ID non valido per modifica:', id);
      return;
    }
    navigateTo(`#/edit/${id}`);
  }, [navigateTo]);

  const goToDelete = useCallback((id) => {
    if (!id || isNaN(id)) {
      console.error('ID non valido per eliminazione:', id);
      return;
    }
    navigateTo(`#/delete/${id}`);
  }, [navigateTo]);

  const goToAdd = useCallback(() => {
    navigateTo('#/new');
  }, [navigateTo]);

  const handleDelete = useCallback(async () => {
    try {
      await apiUtenti.remove(user.authToken, utenteForEdit.id);
      window.location.hash = "#/";
      triggerUpdate();
    } catch (error) {
      console.error("Errore durante l'eliminazione:", error);
    }
  }, [user.authToken, utenteForEdit, history, triggerUpdate]);

  // Opzioni per react-select
  const targetOptions = useMemo(() => {
    return targets.map((target) => ({
      value: Number(parseInt(target.id, 10)),
      label: target.name,
    }));
  }, [targets]);

  const gridKey = useMemo(() => JSON.stringify({ currentPage, pageSize, sorting, rows }), [
    currentPage,
    pageSize,
    sorting,
    rows,
  ]);


  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '300px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Caricamento...</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <DeleteModal
        show={showDeleteModal}
        name={utenteForEdit?.fullname}
        hideCallback={() => (window.location.hash = "#/")}
        deleteCallback={handleDelete}
      />

      {!showEditPage ? (
        <Card className="card-custom gutter-b">
          <Card.Header className="border-0 py-5 row">
            <Card.Title className="float-left col-auto">
              <h3 className="font-weight-bolder text-dark">Dipendenti</h3>
            </Card.Title>
            <TabellaCampioniToolbar addCallback={goToAdd} />
          </Card.Header>
          <Card.Body className="pt-0">
            <Grid key={gridKey} rows={rows} columns={columns}>
              <TargetFormattedColumn for={["targets"]} targets={targets} />
              <AziendaFormattedColumn for={["role"]} />
              <AcquistiFormattedColumn for={["acquisti"]} />
              <FilteringState />
              <IntegratedFiltering getComparatorForType={getComparatorForType} customPredicates={customPredicates} />
              <ActionsColumn editCallback={goToEdit} deleteCallback={goToDelete} />
              <SortingState sorting={sorting} onSortingChange={setSorting} />
              <IntegratedSorting getComparatorForType={getComparatorForType} />
              <PagingState
                currentPage={currentPage}
                onCurrentPageChange={setCurrentPage}
                pageSize={pageSize}
                onPageSizeChange={setPageSize}
              />
              <IntegratedPaging />

              <Table messages={tableMessages} tableComponent={TableComponent} columnExtensions={defaultColumnWidths} />
              <TableHeaderRow showSortingControls />
              <TableFilterRow
                cellComponent={(props) => <FilterCell {...props} targetOptions={targetOptions} />}
                messages={filterRowMessages}
              />
              <PagingPanel messages={pagingPanelMessages} pageSizes={pageSizes} />
            </Grid>
          </Card.Body>
        </Card>
      ) : (
        <EditNewUtente
          token={user.authToken}
          utente={utenteForEdit}
          hideEditPage={() => (window.location.hash = "#/")}
          triggerServerUpdate={triggerUpdate}
        />
      )}
    </>
  );
};
